import { io } from 'socket.io-client';
import { API_URL } from '../config/api';
import { GIFT_EVENTS, SOCKET_NAMESPACE } from '../components/giftDropShape';

// The live feed of a brand's games (gift drops, prize wheels): the API's /style-universe namespace,
// `gift:brand:<brandId>` room (auth.giftOnly + auth.brandId — no star map).
// The handshake carries the raw partner token; the socket does not accept a
// Shopify session token, so the embedded app never calls this and polls.
//
// When the server refuses for good (gift_error gift_drop_brand_forbidden, a
// refused handshake, no namespace on this API) the socket gives up and the
// page keeps polling.

export function styleUniverseUrl(apiUrl = API_URL) {
  try {
    return `${new URL(apiUrl).origin}${SOCKET_NAMESPACE}`;
  } catch {
    return SOCKET_NAMESPACE;
  }
}

/**
 * @param {{ token: string|null, brandId: string,
 *   events?: string[],   the events to relay (default: the gift drops'; the prize wheel page passes PRIZE_WHEEL_EVENTS)
 *   onEvent: (name: string, data: any) => void,
 *   onConnectionChanged?: (connected: boolean) => void,
 *   onRefused?: (code: string|null) => void }} options
 * @returns {{ disconnect(): void }}
 */
export function connectGiftDropsSocket({ token, brandId, events = GIFT_EVENTS, onEvent, onConnectionChanged, onRefused }) {
  if (!token || !brandId) {
    onConnectionChanged?.(false);
    return { connected: false, emitWithAck: () => Promise.reject(Object.assign(new Error('socket_unavailable'), { data: { message: 'socket_unavailable' } })), disconnect() {} };
  }

  const socket = io(styleUniverseUrl(), {
    transports: ['websocket'],
    forceNew: true,
    auth: { token, giftOnly: true, brandId },
    reconnectionDelay: 2000,
    reconnectionDelayMax: 30000,
  });

  let refused = false;
  const giveUp = (code) => {
    refused = true;
    onConnectionChanged?.(false);
    onRefused?.(code ?? null);
    socket.disconnect();
  };

  socket.on('connect', () => onConnectionChanged?.(true));
  socket.on('disconnect', () => onConnectionChanged?.(false));
  socket.on('connect_error', () => {
    onConnectionChanged?.(false);
    // socket.active is false when the server refused the handshake (auth
    // middleware, unknown namespace): socket.io will not retry — nor should we.
    if (!socket.active && !refused) giveUp(null);
  });
  // Not allowed to watch this brand's gifts: the server disconnects next.
  socket.on('gift_error', (data) => giveUp(data?.code));

  for (const name of events) {
    socket.on(name, (data) => onEvent(name, data));
  }

  return {
    get connected() {
      return !!socket.connected;
    },
    /**
     * Emit with an acknowledgement (the prize wheel's draw-now). Resolves with
     * the server's ack; rejects with code `socket_unavailable` while
     * disconnected or `ack_timeout` when no answer comes in `timeoutMs`.
     */
    emitWithAck(name, data, timeoutMs = 8000) {
      return new Promise((resolve, reject) => {
        const fail = (code) => {
          const err = new Error(code);
          err.data = { message: code };
          reject(err);
        };
        if (!socket.connected) {
          fail('socket_unavailable');
          return;
        }
        socket.timeout(timeoutMs).emit(name, data, (err, ack) => {
          if (err) fail('ack_timeout');
          else resolve(ack);
        });
      });
    },
    disconnect() {
      socket.removeAllListeners();
      socket.disconnect();
    },
  };
}
